
import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { RealtimeNotification } from '../../hooks/useRealtimeNotifications';
import { logger } from '../../lib/logger';

interface NotificationsPanelProps {
  notifications: RealtimeNotification[];
  unreadCount: number;
  loading: boolean;
  onMarkAsRead: (id: string) => void;
  onMarkAllAsRead: () => void;
  onDelete: (id: string) => void;
  isOpen: boolean;
  onClose: () => void;
}

const typeConfig: Record<
  string,
  { icon: string; color: string; bg: string }
> = {
  nouvelle_demande: {
    icon: 'ri-mail-send-line',
    color: 'text-teal-600',
    bg: 'bg-teal-50',
  },
  demande: { icon: 'ri-mail-line', color: 'text-teal-600', bg: 'bg-teal-50' },
  alerte: { icon: 'ri-alarm-warning-line', color: 'text-red-600', bg: 'bg-red-50' },
  retard: { icon: 'ri-time-line', color: 'text-orange-600', bg: 'bg-orange-50' },
  systeme: { icon: 'ri-settings-3-line', color: 'text-gray-600', bg: 'bg-gray-100' },
  document: { icon: 'ri-file-text-line', color: 'text-amber-600', bg: 'bg-amber-50' },
  escalade: { icon: 'ri-arrow-up-circle-line', color: 'text-rose-600', bg: 'bg-rose-50' },
  reponse_recue: {
    icon: 'ri-reply-line',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
  },
};

function getTypeConfig(type: string) {
  return (
    typeConfig[type] || {
      icon: 'ri-notification-3-line',
      color: 'text-gray-600',
      bg: 'bg-gray-100',
    }
  );
}

function formatTimeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMin / 60);
  const diffD = Math.floor(diffH / 24);

  if (diffMin < 1) return "À l’instant";
  if (diffMin < 60) return `Il y a ${diffMin} min`;
  if (diffH < 24) return `Il y a ${diffH}h`;
  if (diffD < 7) return `Il y a ${diffD}j`;
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
  });
}

function getPrioriteLabel(priorite: string | null) {
  if (!priorite) return null;
  const map: Record<
    string,
    { label: string; cls: string }
  > = {
    haute: { label: 'Haute', cls: 'bg-red-100 text-red-700' },
    critique: { label: 'Critique', cls: 'bg-red-200 text-red-800' },
    normale: { label: 'Normale', cls: 'bg-gray-100 text-gray-600' },
    basse: { label: 'Basse', cls: 'bg-gray-50 text-gray-500' },
  };
  return map[priorite] || null;
}

export default function NotificationsPanel({
  notifications,
  unreadCount,
  loading,
  onMarkAsRead,
  onMarkAllAsRead,
  onDelete,
  isOpen,
  onClose,
}: NotificationsPanelProps) {
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  /** Close panel when clicking outside */
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        onClose();
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const filtered =
    filter === 'unread'
      ? notifications.filter((n) => !n.read_at)
      : notifications;

  /** Handle click on a notification – mark as read & navigate safely */
  const handleClick = (notif: RealtimeNotification) => {
    try {
      if (!notif.read_at) {
        onMarkAsRead(notif.id);
      }
      if (notif.lien) {
        navigate(notif.lien);
        onClose();
      }
    } catch (err) {
      logger.error('Failed to handle notification click:', err);
    }
  };

  return (
    <div
      ref={panelRef}
      className="absolute right-0 mt-2 w-[400px] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-50"
      style={{ maxHeight: 'calc(100vh - 100px)' }}
    >
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900 text-[15px]">
              Notifications
            </h3>
            {unreadCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 text-[11px] font-bold text-white bg-red-500 rounded-full">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </div>
          {unreadCount > 0 && (
            <button
              onClick={onMarkAllAsRead}
              className="text-xs text-teal-600 hover:text-teal-700 font-medium cursor-pointer whitespace-nowrap"
            >
              Tout marquer comme lu
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex gap-1 bg-gray-100 rounded-full p-1">
          <button
            onClick={() => setFilter('all')}
            className={`flex-1 text-xs font-medium py-1.5 rounded-full transition-all cursor-pointer whitespace-nowrap ${
              filter === 'all'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Toutes ({notifications.length})
          </button>
          <button
            onClick={() => setFilter('unread')}
            className={`flex-1 text-xs font-medium py-1.5 rounded-full transition-all cursor-pointer whitespace-nowrap ${
              filter === 'unread'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Non lues ({unreadCount})
          </button>
        </div>
      </div>

      {/* List */}
      <div className="overflow-y-auto" style={{ maxHeight: '420px' }}>
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full animate-spin mb-3"></div>
            <p className="text-sm text-gray-400">Chargement...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-full mb-3">
              <i className="ri-notification-off-line text-2xl text-gray-400"></i>
            </div>
            <p className="text-sm text-gray-500 font-medium">
              {filter === 'unread'
                ? 'Aucune notification non lue'
                : 'Aucune notification'}
            </p>
            <p className="text-xs text-gray-400 mt-1">Vous êtes à jour !</p>
          </div>
        ) : (
          filtered.map((notif) => {
            const cfg = getTypeConfig(notif.type);
            const isUnread = !notif.read_at;
            const priorite = getPrioriteLabel(notif.priorite);

            return (
              <div
                key={notif.id}
                className={`relative flex gap-3 px-5 py-3.5 transition-colors cursor-pointer group ${
                  isUnread ? 'bg-teal-50/40' : 'bg-white'
                } hover:bg-gray-50`}
                onMouseEnter={() => setHoveredId(notif.id)}
                onMouseLeave={() => setHoveredId(null)}
                onClick={() => handleClick(notif)}
              >
                {/* Unread indicator */}
                {isUnread && (
                  <div className="absolute left-1.5 top-1/2 -translate-y-1/2 w-2 h-2 bg-teal-500 rounded-full"></div>
                )}

                {/* Icon */}
                <div className={`flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg ${cfg.bg}`}>
                  <i className={`${cfg.icon} text-lg ${cfg.color}`}></i>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p
                      className={`text-[13px] leading-snug ${
                        isUnread
                          ? 'font-semibold text-gray-900'
                          : 'font-medium text-gray-700'
                      } line-clamp-1`}
                    >
                      {notif.titre}
                    </p>
                    <span className="text-[11px] text-gray-400 whitespace-nowrap flex-shrink-0 mt-0.5">
                      {formatTimeAgo(notif.created_at)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 leading-relaxed">
                    {notif.message}
                  </p>
                  <div className="flex items-center gap-2 mt-1.5">
                    {priorite && (
                      <span
                        className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${priorite.cls}`}
                      >
                        {priorite.label}
                      </span>
                    )}
                    {notif.lien && (
                      <span className="text-[10px] text-teal-600 font-medium flex items-center gap-0.5">
                        <i className="ri-external-link-line text-[10px]"></i>
                        Voir
                      </span>
                    )}
                  </div>
                </div>

                {/* Hover actions */}
                {hoveredId === notif.id && (
                  <div className="absolute right-3 top-3 flex items-center gap-1">
                    {isUnread && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onMarkAsRead(notif.id);
                        }}
                        className="w-7 h-7 flex items-center justify-center bg-white rounded-md shadow-sm border border-gray-200 hover:bg-teal-50 transition-colors cursor-pointer"
                        title="Marquer comme lu"
                      >
                        <i className="ri-check-line text-sm text-teal-600"></i>
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(notif.id);
                      }}
                      className="w-7 h-7 flex items-center justify-center bg-white rounded-md shadow-sm border border-gray-200 hover:bg-red-50 transition-colors cursor-pointer"
                      title="Supprimer"
                    >
                      <i className="ri-delete-bin-line text-sm text-red-500"></i>
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      {filtered.length > 0 && (
        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/50">
          {/* Using React‑Router <Link> avoids a raw "/" in JSX which caused the parser error */}
          <Link
            to="/notifications-alertes"
            className="flex items-center justify-center gap-1.5 text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
            onClick={onClose}
          >
            Voir toutes les notifications
            <i className="ri-arrow-right-line text-sm"></i>
          </Link>
        </div>
      )}
    </div>
  );
}
